import {useEffect, useState} from 'react'
import {tabs} from 'constants/tabs'
import {useI18n} from 'store/Store'
import {StyledTab, StyledTabs} from './styles'
import TabContent from './TabContent'
import TabLabel from './TabLabel'
import {Box, styled} from '@mui/material'
import Button from 'components/Control/Button'
import {useLocation, useNavigate} from 'react-router-dom'
import LoadingButton from 'components/Control/LoadingButton'
import {useFormContext} from 'react-hook-form'
import {validatedFormFields} from 'constants/tickets'
import {getFullName} from 'services/utils/formUtils'

const getCurrentFields = (currentTab) => validatedFormFields[currentTab] || []

const TabsContainer = ({
    itemName,
    ticket,
    submit,
    response,
    error,
    isLoading,
    filterTabs,
    ...tabProps
}) => {
    const [currentTabIndex, setCurrentTabIndex] = useState(0)
    const [isFormValid, setIsFormValid] = useState(false)
    const {i18n} = useI18n()
    const navigate = useNavigate()
    const {state: elder} = useLocation()
    const formContext = useFormContext()
    const {
        formState: {errors},
    } = formContext
    console.log('errors in TabsContainer', errors)
    const tabsKeys = tabs[itemName].keys.filter(
        (key) => !filterTabs?.includes(key) ?? key !== filterTabs
    )

    const lastTabIndex = tabsKeys.length - 1
    const isLastTab = currentTabIndex === lastTabIndex

    // Check if entire form is valid when navigating to the last tab
    useEffect(() => {
        if (isLastTab) {
            const checkFormValidity = async () => {
                const valid = await formContext.trigger()
                setIsFormValid(valid)
            }
            checkFormValidity()
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isLastTab])

    const isTabValid = () => {
        console.log('formContext', formContext)
        const fields = getCurrentFields(currentTab)
        return fields?.every(
            (field) => !formContext.getFieldState(field)?.error
        )
    }

    const currentTab = tabsKeys[currentTabIndex]
    const isCurrentTabValid = isTabValid(currentTab)
    const fields = getCurrentFields(currentTab)

    const handleChange = async (event, newValue) => {
        const valid = await formContext.trigger(fields)

        const isPreviousTab = newValue < currentTabIndex
        const isNextTab = newValue > currentTabIndex

        if ((valid && isNextTab) || isPreviousTab) {
            setCurrentTabIndex(newValue)
        }
    }

    const getTabAddress = (elder) => {
        if (!elder || !elder.address) {
            return ''
        }
        const {
            address: {street = '', city = ''},
        } = elder
        return `${street} ${city}`
    }

    const getTabTitles = (key) => {
        let subtitle = ''
        const elderName = getFullName(elder ?? ticket?.elder)

        let title = i18n.t(`${itemName}s.${key}`)
        const subtitleKey = tabs[itemName]?.subtitles?.[key]
        subtitle = subtitleKey && i18n.t(`${itemName}s.${subtitleKey}`)

        if (key === 'ticketSource') {
            title = elderName
            subtitle = getTabAddress(elder ?? ticket?.elder)
        }
        return {title, subtitle}
    }

    const findFirstTabWithErrors = () => {
        const errors = formContext.formState.errors
        for (let i = 0; i < tabsKeys.length; i++) {
            const tabKey = tabsKeys[i]
            const tabFields = getCurrentFields(tabKey)
            const hasError = tabFields.some((field) => {
                // Handle nested fields like availableTimes[0].date
                const fieldParts = field.split(/[.[\]]/).filter(Boolean)
                let errorObj = errors
                for (const part of fieldParts) {
                    if (!errorObj) return false
                    errorObj = errorObj[part]
                }
                return !!errorObj
            })
            if (hasError) return i
        }
        return null
    }

    const handleSave = async () => {
        // handleSubmit returns undefined if validation fails
        // We need to check form validity before navigating
        const isFormValid = await formContext.trigger()
        if (isFormValid) {
            await submit()
            navigate('/')
        } else {
            // Navigate to the first tab with validation errors
            const errorTabIndex = findFirstTabWithErrors()
            if (errorTabIndex !== null && errorTabIndex !== currentTabIndex) {
                setCurrentTabIndex(errorTabIndex)
            }
        }
    }

    return (
        <Box sx={{width: '100%'}}>
            <Box sx={{borderBottom: 1, borderColor: 'divider'}}>
                <StyledTabs value={currentTabIndex} onChange={handleChange}>
                    {tabsKeys.map((key) => {
                        const title = getTabTitles(key).title
                        const subtitle = getTabTitles(key).subtitle
                        return (
                            <StyledTab
                                key={key}
                                valid={isCurrentTabValid}
                                label={
                                    <TabLabel
                                        testKey={key}
                                        title={title}
                                        subtitle={subtitle}
                                    />
                                }
                            />
                        )
                    })}
                </StyledTabs>
            </Box>

            {tabsKeys.map((key, index) => {
                const Component = tabs[itemName].components[key]
                return (
                    <TabContent key={key} value={currentTabIndex} index={index}>
                        <Component {...tabProps} />
                    </TabContent>
                )
            })}

            <ButtonsContainer index={currentTabIndex}>
                {currentTabIndex !== 0 && (
                    <Button
                        name="back"
                        color="secondary"
                        onClick={(e) => handleChange(e, currentTabIndex - 1)}>
                        {i18n.t('form.back')}
                    </Button>
                )}
                {currentTabIndex !== lastTabIndex ? (
                    <LoadingButton
                        name="continue"
                        disabled={!isCurrentTabValid}
                        loading={isLoading}
                        onClick={(e) => handleChange(e, currentTabIndex + 1)}>
                        {i18n.t('form.continue')}
                    </LoadingButton>
                ) : (
                    <SummaryButtons>
                        <Button
                            disabled={!isFormValid}
                            color="primary"
                            onClick={handleSave}
                            name="saveAndClose">
                            {i18n.t('form.saveAndClose')}
                        </Button>
                    </SummaryButtons>
                )}
            </ButtonsContainer>
        </Box>
    )
}

export default TabsContainer

const ButtonsContainer = styled('div')`
    display: flex;
    align-items: center;
    justify-content: ${({index}) =>
        index === 0 ? 'flex-end' : 'space-between'};
    width: 100%;
`
const SummaryButtons = styled('div')`
    display: flex;
    gap: 16px;
`
